import { useEffect, useRef } from 'react';

import { IconArrowRight, IconDownload, IconFile, IconX } from './icons';
import { getIncidentCategory } from './incident';
import { IncidentCategoryChip } from './IncidentCategoryChip';
import { IncidentPriorityIndicator } from './IncidentPriorityIndicator';
import { IncidentStatusBadge } from './IncidentStatusBadge';
import { formatAbsoluteDateTime, formatFileSize, formatShortDateTime } from '../../lib/formatters';
import { useExtranetIncidentDetail } from '../../services/api/hooks/useExtranetIncidentDetail';
import type { ExtranetIncidentOutput, IncidentTimelineItemOutput } from '../../types/extranet-api.types';

interface IncidentDetailDrawerProps {
  incidentId: string | null;
  onClose: () => void;
}

export const IncidentDetailDrawer = ({ incidentId, onClose }: IncidentDetailDrawerProps) => {
  const query = useExtranetIncidentDetail(incidentId);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!incidentId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [incidentId, onClose]);

  if (!incidentId) return null;

  const incident = query.data;

  return (
    <div className="fixed inset-0 z-40">
      <div
        onClick={onClose}
        aria-hidden
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="incident-drawer-title"
        className="absolute top-0 right-0 h-full w-full sm:w-[560px] bg-white border-l border-zinc-200 drawer-panel-in flex flex-col"
      >
        {query.isLoading || !incident ? (
          <DrawerSkeleton onClose={onClose} />
        ) : (
          <DrawerContent incident={incident} onClose={onClose} />
        )}
      </div>
    </div>
  );
};

const DrawerContent = ({ incident, onClose }: { incident: ExtranetIncidentOutput; onClose: () => void }) => (
  <>
    <header className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <h2 id="incident-drawer-title" className="font-mono tabular-nums text-lg text-zinc-900 truncate">
          {incident.id}
        </h2>
        <IncidentStatusBadge status={incident.status} />
        {incident.isSecurity ? (
          <span className="inline-flex items-center gap-1.5 rounded-md text-xs font-medium px-2 py-0.5 bg-red-50 text-red-700">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden />
            Sécurité
          </span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
      >
        <IconX width={16} height={16} />
      </button>
    </header>

    <div className="flex-1 overflow-y-auto">
      <Section title="Course">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm text-zinc-900 truncate">{incident.ride.pickupAddress}</div>
            <div className="text-xs text-zinc-500 mt-1 font-mono tabular-nums">
              {formatAbsoluteDateTime(incident.ride.pickupAt)}
            </div>
          </div>
          <IconArrowRight width={14} height={14} className="text-zinc-400 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-zinc-900 truncate">{incident.ride.dropoffAddress}</div>
          </div>
        </div>
        <KeyValueGrid
          items={[
            { label: 'Course', value: incident.rideId, mono: true },
            { label: "Référence GALAC'SI", value: incident.ride.externalRef, mono: true },
            { label: 'Chauffeur', value: incident.ride.driverName },
            { label: 'Ville', value: incident.city },
          ]}
        />
      </Section>

      <Section title="Catégorisation">
        <div className="flex items-center justify-between gap-4">
          <IncidentCategoryChip category={getIncidentCategory(incident)} />
          <IncidentPriorityIndicator priority={incident.priority} />
        </div>
      </Section>

      <Section title="Description">
        <p className="text-sm leading-relaxed text-zinc-700">{incident.clientReport}</p>
      </Section>

      <Section title="Rattachement">
        <KeyValueGrid
          items={[
            { label: 'Entité', value: incident.entityName },
            { label: 'Réf. BUPO', value: incident.siteRef, mono: true },
            { label: 'Contrat', value: incident.contractRef, mono: true },
          ]}
        />
      </Section>

      <Section title="Traitement">
        <Timeline entries={incident.timeline} />
      </Section>

      {incident.allocabResponse ? (
        <Section title="Réponse Allocab">
          <blockquote className="bg-zinc-50 border-l-2 border-zinc-900 px-4 py-3">
            <p className="text-sm leading-relaxed text-zinc-700">{incident.allocabResponse.content}</p>
            <footer className="mt-3 text-xs text-zinc-500 flex items-center gap-2">
              <span className="text-zinc-700">{incident.allocabResponse.submittedBy}</span>
              <span className="text-zinc-300">·</span>
              <span
                className="font-mono tabular-nums"
                title={formatAbsoluteDateTime(incident.allocabResponse.submittedAt)}
              >
                {formatShortDateTime(incident.allocabResponse.submittedAt)}
              </span>
            </footer>
          </blockquote>
        </Section>
      ) : null}

      {incident.attachments.length > 0 ? (
        <Section title={`Pièces jointes (${incident.attachments.length})`}>
          <ul className="flex flex-col">
            {incident.attachments.map(att => (
              <li
                key={att.id}
                className="flex items-center justify-between gap-3 py-2.5 border-b border-zinc-100 last:border-b-0"
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <IconFile width={14} height={14} className="text-zinc-400 shrink-0" />
                  <span className="text-sm text-zinc-900 truncate">{att.filename}</span>
                  <span className="text-xs text-zinc-500 font-mono tabular-nums shrink-0">
                    {formatFileSize(att.sizeBytes)}
                  </span>
                </span>
                <a
                  href={att.url}
                  download={att.filename}
                  className="text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 text-xs transition-colors"
                >
                  <IconDownload width={12} height={12} />
                  <span>Télécharger</span>
                </a>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <div className="h-8" />
    </div>
  </>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="px-6 py-5 border-b border-zinc-100 last:border-b-0">
    <h3 className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 mb-3">{title}</h3>
    <div className="flex flex-col gap-3">{children}</div>
  </section>
);

const KeyValueGrid = ({ items }: { items: { label: string; value: string; mono?: boolean }[] }) => (
  <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5">
    {items.map(item => (
      <div key={item.label} className="flex flex-col gap-0.5 min-w-0">
        <dt className="text-xs text-zinc-500">{item.label}</dt>
        <dd
          className={['text-sm text-zinc-900 truncate', item.mono ? 'font-mono tabular-nums' : ''].join(' ')}
          title={item.value}
        >
          {item.value}
        </dd>
      </div>
    ))}
  </dl>
);

const Timeline = ({ entries }: { entries: IncidentTimelineItemOutput[] }) => {
  const maxStep = Math.max(...entries.map(e => e.step));
  return (
    <ol className="relative">
      {entries.map((entry, idx) => {
        const isCurrent = entry.step === maxStep && entry.label !== 'Clôture du ticket';
        const isLast = idx === entries.length - 1;
        return (
          <li key={`${entry.step}-${entry.at}`} className="relative pl-7 pb-5 last:pb-0">
            {!isLast ? <span className="absolute left-[9px] top-3.5 bottom-0 w-px bg-zinc-200" aria-hidden /> : null}
            <span
              className={[
                'absolute left-0 top-1 inline-flex h-[18px] w-[18px] items-center justify-center rounded-full',
                isCurrent ? 'bg-blue-500 ring-4 ring-blue-100' : 'bg-zinc-900',
              ].join(' ')}
              aria-hidden
            >
              <span className="text-[9px] font-medium text-white font-mono tabular-nums">{entry.step}</span>
            </span>
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm text-zinc-900">{entry.label}</p>
              <p
                className="text-[11px] font-mono tabular-nums text-zinc-500 shrink-0"
                title={formatAbsoluteDateTime(entry.at)}
              >
                {formatShortDateTime(entry.at)}
              </p>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">{entry.actor}</p>
            {entry.note ? <p className="text-xs text-zinc-700 mt-1.5 leading-relaxed">{entry.note}</p> : null}
          </li>
        );
      })}
    </ol>
  );
};

const DrawerSkeleton = ({ onClose }: { onClose: () => void }) => (
  <>
    <header className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="h-5 w-32 rounded-sm skeleton-pulse" />
        <div className="h-5 w-16 rounded-md skeleton-pulse" />
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
      >
        <IconX width={16} height={16} />
      </button>
    </header>
    <div className="flex-1 overflow-y-auto">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="px-6 py-5 border-b border-zinc-100 flex flex-col gap-3">
          <div className="h-3 w-24 rounded-sm skeleton-pulse" />
          <div className="h-4 w-full rounded-sm skeleton-pulse" />
          <div className="h-4 w-4/5 rounded-sm skeleton-pulse" />
        </div>
      ))}
    </div>
  </>
);
